import TranslatableText from "@components/translations/TranslatableText";
import Skeleton from "react-loading-skeleton";
import { useAppSelector } from "@app/hooks";
import { selectMsaJobsByProject } from "@app/features/msa/msaSlice";
import type { RootState } from "@app/store";
import { FaDownload } from "react-icons/fa";
import Button from "@components/button/Button";
import type { MsaCalculationResult, MsaJobStatus } from "@app/features/msa/msaTypes";
import { useTranslation } from "react-i18next";

interface SummaryResultsSectionProps {
  projectId?: string;
}

interface ResultDisplayProps {
  resultData: MsaCalculationResult | null;
  jobId?: string;
  status: MsaJobStatus | undefined;
  error: string | null | undefined;
  type: 'baseline' | 'project';
}

const appendJobIdToUrl = (url: string, jobId?: string) => {
  if (!jobId) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}job_id=${encodeURIComponent(jobId)}`;
};

const ResultDisplay = ({ resultData, jobId, status, error, type }: ResultDisplayProps) => {
  const { t } = useTranslation();
  const isLoading = status === "starting" || status === "in_progress";
  const hasData = resultData?.summary_png_url && resultData?.zip_url;
  const hasFailed = status === "failed";
  const summaryImageUrl = resultData?.summary_png_url
    ? appendJobIdToUrl(resultData.summary_png_url, jobId)
    : undefined;

  const handleDownload = () => {
    if (resultData?.zip_url) {
      window.open(resultData.zip_url, '_blank');
    }
  };

  // Get title and colors based on type
  const titleKey = type === 'baseline' ? 'map.baseline' : 'map.with_project';
  const title = t(titleKey);
  const titleComponent = <TranslatableText translationKey={titleKey} />;
  const colors = type === 'baseline' 
    ? { bg: 'var(--msa-baseline-bg)', border: 'var(--msa-baseline-border)' }
    : { bg: 'var(--msa-project-bg)', border: 'var(--msa-project-border)' };

  return (
    <div className="mb-1">
      {/* Section Title */}
      <h4 className="fs-16 ff-medium mb-2">{titleComponent}</h4>

      {/* Error State */}
      {hasFailed && (
        <div className="mb-2" style={{ 
          padding: '8px 12px', 
          fontSize: '14px',
          backgroundColor: '#fde8e8',
          border: '1px solid #f5c2c2',
          borderRadius: '6px',
          color: '#9c0004'
        }}>
          <TranslatableText translationKey="map.msa_calculation_failed" />: {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="mb-2" style={{ 
          padding: '8px 12px', 
          fontSize: '14px',
          backgroundColor: '#fff9e6',
          border: '1px solid #ffe8b3',
          borderRadius: '6px',
          color: '#b8860b'
        }}>
          <TranslatableText translationKey="map.msa_calculation_in_progress" />
        </div>
      )}

      {/* No Data State */}
      {!isLoading && !hasData && !hasFailed && (
        <div className="mb-2" style={{ 
          padding: '8px 12px', 
          fontSize: '14px',
          backgroundColor: '#e5ecf4',
          border: '1px solid #b8d4e8',
          borderRadius: '6px',
          color: '#164777'
        }}>
          <TranslatableText translationKey="map.msa_no_results_available" translationParams={{ title }} />
        </div>
      )}

      {/* Download Button */}
      {isLoading ? (
        <div className="mb-3">
          <Skeleton height={40} width={200} borderRadius={6} />
        </div>
      ) : hasData ? (
        <Button
          onClick={handleDownload}
          className="d-flex align-items-center gap-8 w-fit mb-3"
          style={{
            padding: '10px 16px',
            backgroundColor: colors.bg,
            border: `1px solid ${colors.border}`,
            color: '#666',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          whileTapProps={{
            backgroundColor: type === 'baseline' ? '#e0e0e0' : '#1f5066'
          }}
        >
          <FaDownload size={16} />
          <TranslatableText translationKey="map.download_results" />
        </Button>
      ) : null}

      {/* Summary Image */}
      {isLoading ? (
        <div className="pt-2">
          <Skeleton height={400} borderRadius={8} />
        </div>
      ) : hasData && resultData?.summary_png_url ? (
        <div>
          <img 
            src={summaryImageUrl} 
            alt={t('map.msa_summary_results_alt', { title })}
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

const SummaryResultsSection = ({ projectId }: SummaryResultsSectionProps) => {
  const msaJobs = useAppSelector((state: RootState) => 
    projectId ? selectMsaJobsByProject(state, projectId) : null
  );

  return (
    <div className="msa-container">
      <h3 className="fs-18 ff-medium mb-1">
        <TranslatableText translationKey="map.summary_results" />
      </h3>

      {/* Baseline Results */}
      <ResultDisplay
        resultData={msaJobs?.baseline?.result || null}
        jobId={msaJobs?.baseline?.jobId}
        status={msaJobs?.baseline?.status}
        error={msaJobs?.baseline?.error}
        type="baseline"
      />

      {/* Project Results */}
      <ResultDisplay
        resultData={msaJobs?.project?.result || null}
        jobId={msaJobs?.project?.jobId}
        status={msaJobs?.project?.status}
        error={msaJobs?.project?.error}
        type="project"
      />
    </div>
  );
};

export default SummaryResultsSection;
